import { history } from 'instantsearch.js/es/lib/routers';
import { InstantSearchOptions, UiState } from 'instantsearch.js/es/types';
import {
  ProfilesDaoSimProfileDetails,
  StructsBillingRateResponse,
} from 'types/__generated__/SimInventoryApi';
import { AlgoliaMetaResponse, RAT } from './searchTypes';

export const ALGOLIA_INDEX = 'networks-v2';
export const ALGOLIA_INDEX_NAME_ASC = 'networks-v2_name_asc';
export const ALGOLIA_INDEX_NAME_DESC = 'networks-v2_name_desc';
export const ALGOLIA_INDEX_COUNTRY_ASC = 'networks-v2_country_asc';
export const ALGOLIA_INDEX_COUNTRY_DESC = 'networks-v2_country_desc';
export const ALGOLIA_INDEX_COUNTRY_UNIQ = 'networks-v2_country_uniq';
export const ALGOLIA_INDEX_DATA_RATE_ASC = 'networks-v2_rate_asc';
export const ALGOLIA_INDEX_DATA_RATE_DESC = 'networks-v2_rate_desc';

// We can turn these into env variables IF we need to create a separate staging/test index
export const ALGOLIA_APP_ID = 'UKV1ENAXDY';
export const ALGOLIA_SEARCH_KEY = '037e3268ecd8a9d239dbf22280879ad3';

// The order we want RATs to appear in a list
export const ratDisplayOrder: RAT[] = ['NR', 'LTE', 'CAT-M1', 'NB-IOT', 'WCDMA', 'CDMA', 'GSM'];

// Function that sorts a list of RATs into a logical order
export function reorderRATs(rats: RAT[]) {
  return rats.sort((a, b) => ratDisplayOrder.indexOf(a) - ratDisplayOrder.indexOf(b));
}

/**
 * In the data model, RATs are represented as "GSM", "CDMA", etc.
 * but we want to present to the user as "2G", "3G", etc.
 * This object specifies the translations
 */
export const properRatNames: Record<string, string | { short: string; long: string }> = {
  GSM: '2G',
  CDMA: '2G/3G',
  WCDMA: '3G',
  LTE: '4G LTE',
  NR: '5G',
  'CAT-M1': { short: 'LTE-M', long: 'LTE-M/Cat-M1' },
  'NB-IOT': 'NB-IoT',
};

/**
 * Converts a database RAT (eg. CDMA) into a user-friendly RAT
 */
export function transformRAT(rat: RAT, variant?: 'short' | 'long') {
  const match = properRatNames[rat];
  if (typeof match === 'string') return match;
  if (typeof match === 'object') {
    if (variant && match[variant]) return match[variant];
    return match.short;
  }
  return '';
}

/**
 * Converts an Algolia refinement RAT value to user-friendly querystring value with no hyphens, etc
 * eg. "WCDMA" => "3G"
 */
function ratToRoute(rat: string) {
  return transformRAT(rat as RAT, 'short').replaceAll(/[^0-9A-z]/g, '');
}

/**
 * Converts a querystring RAT value into its corresponding Algolia RAT value
 * eg. "3G" => "WCDMA"
 */
function routeToRat(rat: string) {
  return (
    Object.keys(properRatNames).find(
      (key) =>
        properRatNames[key] === rat ||
        (typeof properRatNames[key] === 'object' &&
          (properRatNames[key].short.replaceAll(/[^0-9A-z]/g, '') === rat ||
            properRatNames[key].long.replaceAll(/[^0-9A-z]/g, '') === rat))
    ) || ''
  );
}

function offerIdToProfileId(simProfiles: ProfilesDaoSimProfileDetails[], offerId?: string) {
  if (!offerId) return null;
  return simProfiles.find((profile) => profile.offerId === parseFloat(offerId))?.simProfileId;
}
function profileIdToOfferId(profileId: string, simProfiles: ProfilesDaoSimProfileDetails[]) {
  if (!profileId) return null;
  return (
    simProfiles
      .find((profile) => profile.simProfileId === parseFloat(profileId))
      ?.offerId?.toString() ?? null
  );
}

/**
 * Converts search state into a querystring and vice-versa
 * This allows us to deeplink to a pre-populated search results table
 */
export const instantsearchRouting = (
  simProfiles: ProfilesDaoSimProfileDetails[]
): InstantSearchOptions['routing'] => ({
  router: history({ cleanUrlOnDispose: true }),
  stateMapping: {
    stateToRoute: (uiState): Record<string, any> => {
      const indexState = uiState[ALGOLIA_INDEX];

      return {
        search: indexState.query,
        rats: indexState.refinementList?.supported_rat
          ? indexState.refinementList.supported_rat.map((rat) => ratToRoute(rat)).join('|')
          : undefined,
        profile: indexState.menu?.offer_id
          ? offerIdToProfileId(simProfiles, indexState.menu?.offer_id)
          : undefined,
      };
    },
    routeToState: (routeState: any): UiState => {
      // have to use `any` type here or it gets angry
      const state: UiState = {
        [ALGOLIA_INDEX]: {
          query: routeState.search,
        },
      };

      if (routeState.rats) {
        state[ALGOLIA_INDEX].refinementList = {};
        state[ALGOLIA_INDEX].refinementList.supported_rat = routeState.rats
          .split('|')
          .map((rat: string) => routeToRat(rat));
      }

      if (routeState.profile) {
        state[ALGOLIA_INDEX].menu = {};
        state[ALGOLIA_INDEX].menu.offer_id =
          profileIdToOfferId(routeState.profile, simProfiles) ?? '';
      }

      return state;
    },
  },
});

/**
 * Calculates the minimum and maximum data rates based on billing and network metadata.
 * Handles flat rates and ranges, filtering invalid or non-positive rates for minimum calculation.
 */
export function calculateRateRange(
  billingRates: StructsBillingRateResponse,
  meta: AlgoliaMetaResponse | undefined,
  defaultRate?: number // either the plan or simBilling default rate
): { rateMin: number; rateMax: number; isFlatRate: boolean } {
  // Default state or if essential data is missing
  const defaultReturn = { rateMin: 0, rateMax: 0, isFlatRate: true };

  if (!billingRates && !defaultRate) {
    return defaultReturn;
  }

  // Handle flat rate scenario
  if (billingRates.rateType === 'flat_rate') {
    const rate = Math.max(0, defaultRate ?? 0);
    return {
      rateMin: rate,
      rateMax: rate,
      isFlatRate: true,
    };
  }

  // Merge billing rates and meta data rates to determine the range
  const mergedRates = meta?.dataRates.map((rate) => {
    const billingRate = billingRates.data?.find((br) => br?.unc === rate.objectID);
    if (!billingRate && !rate.data_rate) return null;
    if (!billingRate) return rate.data_rate;
    if (!rate.data_rate) return billingRate.costPerMb;
    return Math.min(billingRate.costPerMb ?? Infinity, rate.data_rate);
  });

  // Sort merged rates from lowest to highest, excluding nulls
  const sortedRates = mergedRates
    ?.filter((rate): rate is number => rate !== null)
    .sort((a, b) => a - b);

  if (!sortedRates) return defaultReturn;

  return {
    rateMin: sortedRates[0],
    rateMax: sortedRates[sortedRates.length - 1],
    // Considered flat rate if min equals max
    isFlatRate: sortedRates[0] === sortedRates[sortedRates.length - 1],
  };
}
